/**
 * ESTADOS — MAPEO CENTRAL (UX-1C)
 *
 * Única fuente de las etiquetas (sentence case) y del TONO visual de cada
 * estado de workflow. La base de datos conserva sus enums; aquí sólo se
 * decide cómo se LEEN. Ninguna página inventa su propia paleta ni su texto.
 *
 * Tres conceptos que nunca comparten mecanismo visual:
 *   · DOMINIO (Limpieza / Mantenimiento / Inventario / Calidad) → marcador
 *     pequeño de dominio (`MarcaDominio`: «OT», «PT»…).
 *   · ESTADO (posición en el workflow) → `StatusBadge` + acento de tarjeta.
 *   · SEVERIDAD (aviso, bloqueo, conflicto) → notas/alertas con ⚠, aparte.
 *
 * Tonos (progresión, no «otro color»):
 *   neutral   sin empezar / borrador — nada que mirar todavía.
 *   info      planificado: decidido, aún no empezado.
 *   active    en marcha (más intensidad que info).
 *   completed hecho, pendiente de verificar o cerrar (verde suave).
 *   success   verificado / validado / vigente con éxito (verde pleno).
 *   warning   requiere actuación (pendiente, en espera, por validar).
 *   reopen    estaba terminado y vuelve a requerir atención (aviso fuerte).
 *   danger    fallo real: no realizada, NO OK, rechazado.
 *   closed    terminal administrativo: baja peso, nunca compite con lo activo.
 *   void      sin vigencia (anulado/cancelado): NUNCA parece peligro activo.
 *
 * Aquí no hay permisos ni transiciones: sólo presentación.
 */

export type Tono = 'neutral' | 'info' | 'active' | 'completed' | 'success' | 'warning' | 'reopen' | 'danger' | 'closed' | 'void';

export const TONOS: readonly Tono[] = ['neutral', 'info', 'active', 'completed', 'success', 'warning', 'reopen', 'danger', 'closed', 'void'];

export interface DescEstado {
  readonly label: string;
  readonly tone: Tono;
}

type Mapa = Readonly<Record<string, DescEstado>>;
const e = (label: string, tone: Tono): DescEstado => ({ label, tone });

/** Código desconocido (API más nueva que el cliente): legible y neutro, nunca el enum en mayúsculas. */
export function etiquetaLegible(codigo: string): string {
  const t = codigo.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function desc(mapa: Mapa, codigo: string | null | undefined): DescEstado {
  if (codigo === null || codigo === undefined || codigo === '') return e('—', 'neutral');
  return mapa[codigo] ?? e(etiquetaLegible(codigo), 'neutral');
}

// ---------------------------------------------------------------- Limpieza

/** OT de limpieza. El orden de las claves es el orden del workflow (leyendas, selects). */
export const ESTADOS_OT: Mapa = {
  PLANIFICADA: e('Planificada', 'info'),
  ASIGNADA: e('Asignada', 'info'),
  EN_CURSO: e('En curso', 'active'),
  COMPLETADA: e('Completada', 'completed'),
  VERIFICADA: e('Verificada', 'success'),
  CERRADA: e('Cerrada', 'closed'),
  REABIERTA: e('Reabierta', 'reopen'),
  NO_REALIZADA: e('No realizada', 'danger'),
  ANULADA: e('Anulada', 'void'),
  //  Legado (pre-FASE 4): sólo para leer históricos.
  VALIDADA: e('Validada', 'success'),
  RECHAZADA: e('Rechazada', 'danger'),
};
export const estadoOtLimpieza = (c: string | null | undefined): DescEstado => desc(ESTADOS_OT, c);

/** Línea de OT. NO_APLICA no es un fallo: neutro; NO_OK sí lo es. */
export const ESTADOS_LINEA: Mapa = {
  PENDIENTE: e('Pendiente', 'neutral'),
  CONFIRMADA: e('Confirmada', 'completed'),
  VERIFICADA: e('Verificada', 'success'),
  NO_OK: e('No OK', 'danger'),
  NO_APLICA: e('No aplica', 'neutral'),
};
export const estadoLineaOt = (c: string | null | undefined): DescEstado => desc(ESTADOS_LINEA, c);

/** Plan de limpieza (versión). */
export const ESTADOS_PLAN_LIMPIEZA: Mapa = {
  BORRADOR: e('Borrador', 'neutral'),
  VIGENTE: e('Vigente', 'success'),
  SUSTITUIDO: e('Sustituido', 'closed'),
};
export const estadoPlanLimpieza = (c: string | null | undefined): DescEstado => desc(ESTADOS_PLAN_LIMPIEZA, c);

// ----------------------------------------------------------- Mantenimiento

/** PT. ABIERTO / PLANIFICADO / EN_CURSO: gris → azul suave → azul intenso (no tres azules iguales). */
export const ESTADOS_PT: Mapa = {
  ABIERTO: e('Abierto', 'neutral'),
  PLANIFICADO: e('Planificado', 'info'),
  EN_CURSO: e('En curso', 'active'),
  PENDIENTE: e('Pendiente', 'warning'),
  FINALIZADO: e('Finalizado', 'completed'),
  CERRADO: e('Cerrado', 'closed'),
  REABIERTO: e('Reabierto', 'reopen'),
  ANULADO: e('Anulado', 'void'),
};
export const estadoPtMantenimiento = (c: string | null | undefined): DescEstado => desc(ESTADOS_PT, c);

export const ESTADOS_INCIDENCIA_MNT: Mapa = {
  ABIERTA: e('Abierta', 'warning'),
  EN_REVISION: e('En revisión', 'active'),
  RESUELTA: e('Resuelta', 'success'),
  CANCELADA: e('Cancelada', 'void'),
};
export const estadoIncidenciaMnt = (c: string | null | undefined): DescEstado => desc(ESTADOS_INCIDENCIA_MNT, c);

export const ESTADOS_PLAN_PREVENTIVO: Mapa = {
  BORRADOR: e('Borrador', 'neutral'),
  VIGENTE: e('Vigente', 'success'),
  PAUSADO: e('Pausado', 'warning'),
  CERRADO: e('Cerrado', 'closed'),
};
export const estadoPlanPreventivo = (c: string | null | undefined): DescEstado => desc(ESTADOS_PLAN_PREVENTIVO, c);

export const ESTADOS_ACTIVO: Mapa = {
  OPERATIVO: e('Operativo', 'success'),
  FUERA_SERVICIO: e('Fuera de servicio', 'warning'),
  BAJA: e('Baja', 'closed'),
};
export const estadoActivo = (c: string | null | undefined): DescEstado => desc(ESTADOS_ACTIVO, c);

// -------------------------------------------------------------- Inventario

export const ESTADOS_INVENTARIO: Mapa = {
  BORRADOR: e('Borrador', 'neutral'),
  EN_CONTEO: e('En conteo', 'active'),
  PENDIENTE_VALIDACION: e('Pendiente de validación', 'warning'),
  CERRADO: e('Cerrado', 'closed'),
  ANULADO: e('Anulado', 'void'),
};
export const estadoInventario = (c: string | null | undefined): DescEstado => desc(ESTADOS_INVENTARIO, c);

export const ESTADOS_OPERACION_STOCK: Mapa = {
  BORRADOR: e('Borrador', 'neutral'),
  CONFIRMADA: e('Confirmada', 'success'),
  ANULADA: e('Anulada', 'void'),
};
export const estadoOperacionStock = (c: string | null | undefined): DescEstado => desc(ESTADOS_OPERACION_STOCK, c);

export const ESTADOS_CONSUMO_PT: Mapa = {
  BORRADOR: e('Borrador', 'neutral'),
  PENDIENTE_VALIDACION: e('Pendiente de validación', 'warning'),
  VALIDADO: e('Validado', 'success'),
  RECHAZADO: e('Rechazado', 'danger'),
  ANULADO: e('Anulado', 'void'),
};
export const estadoConsumoPt = (c: string | null | undefined): DescEstado => desc(ESTADOS_CONSUMO_PT, c);

/** FASE 9.6-A: consumos de inventario de una OT de Limpieza (sin borrador). */
export const ESTADOS_CONSUMO_OT: Mapa = {
  PENDIENTE_VALIDACION: e('Pendiente de validación', 'warning'),
  VALIDADO: e('Validado', 'success'),
  RECHAZADO: e('Rechazado', 'danger'),
  ANULADO: e('Anulado', 'void'),
};
export const estadoConsumoOt = (c: string | null | undefined): DescEstado => desc(ESTADOS_CONSUMO_OT, c);

/** Artículo / maestro: activo o inactivo (no es un workflow). */
export const estadoArticulo = (activo: boolean): DescEstado => (activo ? e('Activo', 'success') : e('Inactivo', 'neutral'));

// ------------------------------------------------------------------ Calidad

export const ESTADOS_NC: Mapa = {
  ABIERTA: e('Abierta', 'warning'),
  EN_TRATAMIENTO: e('En tratamiento', 'active'),
  CERRADA: e('Cerrada', 'closed'),
  ANULADA: e('Anulada', 'void'),
};
export const estadoNc = (c: string | null | undefined): DescEstado => desc(ESTADOS_NC, c);

export const ESTADOS_INCIDENCIA_CALIDAD: Mapa = {
  ABIERTA: e('Abierta', 'warning'),
  EN_ANALISIS: e('En análisis', 'active'),
  RESUELTA: e('Resuelta', 'completed'),
  CERRADA: e('Cerrada', 'closed'),
  CANCELADA: e('Cancelada', 'void'),
};
export const estadoIncidenciaCalidad = (c: string | null | undefined): DescEstado => desc(ESTADOS_INCIDENCIA_CALIDAD, c);

export const ESTADOS_ACCION_CORRECTIVA: Mapa = {
  PENDIENTE: e('Pendiente', 'warning'),
  EN_CURSO: e('En curso', 'active'),
  COMPLETADA: e('Completada', 'success'),
  CANCELADA: e('Cancelada', 'void'),
};
export const estadoAccionCorrectiva = (c: string | null | undefined): DescEstado => desc(ESTADOS_ACCION_CORRECTIVA, c);

// ------------------------------------------------------------------ utilidades

/** Pares [código, etiqueta] en orden de workflow, para `<option>` sin repetir textos. */
export function opcionesDe(mapa: Mapa, excluir: readonly string[] = []): [string, string][] {
  return Object.entries(mapa).filter(([k]) => !excluir.includes(k)).map(([k, d]) => [k, d.label]);
}

/** Terminales: bajan peso visual en planners y listados (siguen legibles). */
export const esTerminal = (tone: Tono): boolean => tone === 'closed' || tone === 'void';
/** Requieren atención: marcador propio (no sólo «otro color»). */
export const requiereAtencion = (tone: Tono): boolean => tone === 'warning' || tone === 'reopen' || tone === 'danger';
